import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { ServicePaymentMedicalProceduresStore } from './service-payment-medical-procedures.store';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { GridInput } from '@it2go/data-grid';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { debounceTime } from 'rxjs/operators';
import config from '../../../../../../config/config';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ServicePaymentMedicalProcedureGridItemObject } from '@it2go/types';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import { TableColumn } from '../../../../../shared/components/kit/table/table.types';
import { FieldErrorFactory } from '../../../../../shared/service/field-error.service';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { AsyncPipe } from '@angular/common';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { NumberInputComponent } from '../../../../../../components/kit/input/text-input/number-input.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { FormatServiceProcedurePipe } from '../../../../../shared/pipes/format-service-procedure.pipe';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';

@Component({
    selector: 'app-service-payment-medical-procedures',
    templateUrl: './service-payment-medical-procedures.component.html',
    providers: [ServicePaymentMedicalProceduresStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableComponent,
        AsyncPipe,
        PrimeTemplate,
        NumberInputComponent,
        Dialog,
        TranslatePipe,
        SelectInputComponent,
        FormatServiceProcedurePipe,
        ActionButtonsComponent,
    ],
})
export class ServicePaymentMedicalProceduresComponent extends SubscriptionComponent {

    protected readonly store = inject(ServicePaymentMedicalProceduresStore);
    private readonly fb = inject(FormBuilder);
    private readonly confirmBuilder = inject(ConfirmBuilder);

    @Input({ required: true }) serviceId!: number;
    @Input({ required: true }) servicePaymentId!: number;

    searchCurrentValue: string | null = null;
    searchSubject = new Subject<string>();

    addNewVisible = false;

    form = this.fb.group({
        code: this.fb.control<string | null>(null, Validators.required, this.procedureDuplicateValidator()),
        priceForPoint: this.fb.control<number | null>(null, Validators.required),
    });
    inlineForms: FormGroup[] = [];

    columns: TableColumn[] = [
        {
            name: 'code',
            filterable: true,
        },
        {
            name: 'description',
            filterable: true,
        },
        {
            name: 'priceForPoint',
            filterable: {
                type: 'number',
            },
        },
    ];

    constructor() {
        super();
        this.store.refreshGrid(
            'getServicePaymentMedicalProcedures',
            'createServicePaymentMedicalProcedure',
            'deleteServicePaymentMedicalProcedure',
        );
        this.store.registerSuccessMessage('createServicePaymentMedicalProcedure', 'common.created');
        this.store.registerSuccessMessage('updateServicePaymentMedicalProcedure', 'common.updated');
        this.store.registerSuccessMessage('deleteServicePaymentMedicalProcedure', 'common.deleted');

        this.subs.push(
            this.searchSubject
                .pipe(debounceTime(config.debounceTime))
                .subscribe((search) => this.loadMedicalProcedures(search)),
            this.store.servicePaymentMedicalProcedures$.subscribe((it) => {
                if (it) {
                    this.generateForms(it);
                }
            }),
        );
    }

    protected openAddNew() {
        this.addNewVisible = true;
        this.loadMedicalProcedures(null);
    }

    protected filterMedicalProcedures(search: string): void {
        this.searchCurrentValue = search;
        this.searchSubject.next(search);
    }

    protected create() {
        if (isInvalid(this.form)) return;

        this.store.api.createServicePaymentMedicalProcedure({
            servicePaymentId: this.servicePaymentId,
            code: this.form.value.code!,
            priceForPoint: Number(this.form.value.priceForPoint!),
        });

        this.addNewVisible = false;
        this.form.reset();
    }

    protected delete(item: any) {
        this.confirmBuilder.confirm(
            'service.payments.medicalProceduresPrices.deleteConfirmationHeader',
            'service.payments.medicalProceduresPrices.deleteConfirmationLabel',
            () => this.store.api.deleteServicePaymentMedicalProcedure({ id: item.item.id }),
            {
                name: 'servicePaymentMedicalProcedureDeleteConfirm' + item.item.id,
            },
            'pi-trash',
            item.event.target,
        );
    }

    private async loadMedicalProcedures(search: string | null) {
        const input: GridInput = {
            sorter: [{ column: 'code', direction: 'ASC' }],
            paging: { page: 1, itemsPerPage: 100 },
            filter: [[{
                column: 'serviceId',
                operator: 'EQ',
                value: [this.serviceId.toString()],
            }]],
        };

        if (search) {
            input.search = [search];
        }

        this.store.api.getServiceMedicalProcedures(input);
    }

    private generateForms(items: ServicePaymentMedicalProcedureGridItemObject[]) {
        this.inlineForms = [];
        items.forEach((it) => {
            const fg = this.fb.group({
                inlinePrice: this.fb.control<number>(it.priceForPoint, Validators.required),
            });

            this.subs.push(
                fg.controls.inlinePrice.valueChanges
                    .pipe(debounceTime(config.debounceTime))
                    .subscribe((newPrice) => {
                        if (isInvalid(fg)) return;

                        this.store.api.updateServicePaymentMedicalProcedure({
                            id: it.id,
                            priceForPoint: Number(newPrice ?? 0),
                        });
                    }),
            );

            this.inlineForms.push(fg);
        });
    }

    private procedureDuplicateValidator(): AsyncValidatorFn {
        return async (control: AbstractControl): Promise<ValidationErrors | null> => {
            if (control.value && this.servicePaymentId) {
                this.loadServicePaymentMedicalProcedure(control.value);
                const duplicateCheck = await this.store.get(this.store.checkDuplicateResponse);
                if (duplicateCheck && duplicateCheck.length) {
                    return FieldErrorFactory.servicePaymentMedicalProcedureDuplicateError();
                } else {
                    return null;
                }
            }
            return null;
        };
    }

    private loadServicePaymentMedicalProcedure(code: string) {
        const filter = new FilterBuilder();
        filter.where('servicePaymentId', this.servicePaymentId);
        filter.where('code', code);
        filter.limit(1);
        this.store.checkDuplicate(filter.filter());
    }
}
